import { Controller } from "@hotwired/stimulus"

// Pagy renders every nav in the app as <nav class="pagy series-nav"> regardless of call-site
// options, so this one selector covers all pagination. Disabled page/gap items carry no href.
const PAGINATION_LINK_SELECTOR = "nav.pagy a[href]"
const SCROLLABLE_OVERFLOW = ["auto", "scroll", "overlay"]
const PENDING_TIMEOUT_MS = 5000

export default class extends Controller {
  connect() {
    this.pendingContainer = null
    this.pendingFrame = null
    this.boundScroll = this.scrollToTop.bind(this)
    this.boundDisarm = this.disarm.bind(this)
    // Frame events target <turbo-frame> and do not bubble to document; capture phase
    // runs from document downward so we still see turbo:frame-render / turbo:frame-load.
    this.frameCaptureOptions = { capture: true }

    document.addEventListener("turbo:frame-render", this.boundScroll, this.frameCaptureOptions)
    document.addEventListener("turbo:frame-load", this.boundScroll, this.frameCaptureOptions)
    document.addEventListener("turbo:before-stream-render", this.boundScroll)
    document.addEventListener("turbo:load", this.boundScroll)
    document.addEventListener("turbo:fetch-request-error", this.boundDisarm)
  }

  disconnect() {
    this.disarm()
    document.removeEventListener("turbo:frame-render", this.boundScroll, this.frameCaptureOptions)
    document.removeEventListener("turbo:frame-load", this.boundScroll, this.frameCaptureOptions)
    document.removeEventListener("turbo:before-stream-render", this.boundScroll)
    document.removeEventListener("turbo:load", this.boundScroll)
    document.removeEventListener("turbo:fetch-request-error", this.boundDisarm)
  }

  arm(event) {
    const link = event.target.closest(PAGINATION_LINK_SELECTOR)
    if (!link) return

    this.pendingContainer = this.scrollContainerFor(link)
    this.pendingFrame = link.closest("turbo-frame")
    this.armTimeout()
  }

  scrollToTop(event) {
    if (!this.pendingContainer) return
    if (this.unrelatedFrameEvent(event)) return

    const container = this.pendingContainer
    this.disarm()

    requestAnimationFrame(() => {
      container.scrollTo({ top: 0, behavior: "auto" })
    })
  }

  disarm() {
    this.pendingContainer = null
    this.pendingFrame = null
    this.cancelTimeout()
  }

  unrelatedFrameEvent(event) {
    if (event?.type !== "turbo:frame-render" && event?.type !== "turbo:frame-load") return false
    if (!this.pendingFrame) return false

    return event.target !== this.pendingFrame
  }

  armTimeout() {
    this.cancelTimeout()
    this.timeoutId = window.setTimeout(this.boundDisarm, PENDING_TIMEOUT_MS)
  }

  cancelTimeout() {
    if (!this.timeoutId) return

    window.clearTimeout(this.timeoutId)
    this.timeoutId = null
  }

  scrollContainerFor(link) {
    let insideFixedOverlay = false
    let node = link.parentElement

    while (node && node !== document.body) {
      if (this.scrollsVertically(node)) return node
      if (this.fixed(node)) insideFixedOverlay = true

      node = node.parentElement
    }

    // A modal body with nothing to scroll must not fall through to the window, or the page
    // behind the modal would move instead.
    return insideFixedOverlay ? null : window
  }

  scrollsVertically(node) {
    if (node.scrollHeight <= node.clientHeight) return false

    return SCROLLABLE_OVERFLOW.includes(window.getComputedStyle(node).overflowY)
  }

  fixed(node) {
    return window.getComputedStyle(node).position === "fixed"
  }
}
